'use client';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  UseFormSetError,
  UseFormClearErrors,
  UseFormWatch,
  FieldErrors,
  Path,
  // PathValue,
  FieldValues,
} from 'react-hook-form';
import { z } from 'zod';

import { ProgressiveValidator } from '../utils/validation';

// Validation state hook
export function useValidationState<T extends FieldValues>() {
  const [errors, setErrors] = useState<FieldErrors<T>>({});
  const [isValidating, setIsValidating] = useState(false);
  const [touchedFields, setTouchedFields] = useState<Set<Path<T>>>(new Set());

  const addError = useCallback((field: Path<T>, error: string) => {
    setErrors((prev) => ({
      ...prev,
      [field]: { message: error },
    }));
  }, []);

  const removeError = useCallback((field: Path<T>) => {
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  }, []);

  const clearAllErrors = useCallback(() => {
    setErrors({});
  }, []);

  const touchField = useCallback((field: Path<T>) => {
    setTouchedFields((prev) => new Set(prev).add(field));
  }, []);

  return {
    errors,
    isValidating,
    touchedFields,
    setIsValidating,
    addError,
    removeError,
    clearAllErrors,
    touchField,
  };
}

// Async validation hook
export function useAsyncValidation<T = unknown>(
  validator: (value: T) => Promise<boolean | { isValid: boolean; message?: string }>,
  options?: {
    debounceMs?: number;
    validateOnMount?: boolean;
  }
) {
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const validate = useCallback(
    async (value: T) => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      return new Promise<boolean>((resolve) => {
        debounceTimer.current = setTimeout(async () => {
          setIsValidating(true);
          setError(null);

          try {
            const result = await validator(value);

            if (typeof result === 'boolean') {
              if (!result) {
                setError('Validation failed');
              }
              resolve(result);
            } else {
              if (!result.isValid && result.message) {
                setError(result.message);
              }
              resolve(result.isValid);
            }
          } catch {
            setError('Validation error occurred');
            resolve(false);
          } finally {
            setIsValidating(false);
          }
        }, options?.debounceMs || 300);
      });
    },
    [validator, options?.debounceMs]
  );

  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  return {
    validate,
    isValidating,
    error,
    clearError: () => setError(null),
  };
}

// Cross-field validation hook
export function useCrossFieldValidation<T extends FieldValues>(
  watch: UseFormWatch<T>,
  setError: UseFormSetError<T>,
  clearErrors: UseFormClearErrors<T>,
  validations: Array<{
    fields: Path<T>[];
    validate: (values: unknown[]) => { field: Path<T>; message: string } | null;
    trigger?: 'onChange' | 'onBlur';
  }>
) {
  const watchedValues = validations.reduce(
    (acc, validation) => {
      validation.fields.forEach((field) => {
        acc[field] = watch(field);
      });
      return acc;
    },
    {} as Record<Path<T>, unknown>
  );

  useEffect(() => {
    validations.forEach((validation) => {
      const values = validation.fields.map((field) => watchedValues[field]);
      const error = validation.validate(values);

      if (error) {
        setError(error.field, { message: error.message });
      } else {
        validation.fields.forEach((field) => clearErrors(field));
      }
    });
  }, [watchedValues, validations, setError, clearErrors]);
}

// Progressive validation hook
export function useProgressiveValidation<T extends FieldValues>() {
  const validatorRef = useRef(new ProgressiveValidator<T>());
  const [validationErrors, setValidationErrors] = useState<Partial<Record<Path<T>, string>>>({});

  const addFieldValidation = useCallback(
    (field: Path<T>, validation: () => boolean | { isValid: boolean; message?: string }) => {
      validatorRef.current.addValidation(field as keyof T, () => {
        const result = validation();
        if (typeof result === 'boolean') return result;

        if (!result.isValid && result.message) {
          setValidationErrors((prev) => ({
            ...prev,
            [field]: result.message,
          }));
        } else {
          setValidationErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[field];
            return newErrors;
          });
        }

        return result.isValid;
      });
    },
    []
  );

  const validateField = useCallback((field: Path<T>) => {
    validatorRef.current.touch(field as keyof T);
    const isValid = validatorRef.current.validate(field as keyof T);
    return isValid;
  }, []);

  const validateAllFields = useCallback(() => {
    return validatorRef.current.validateAll();
  }, []);

  const resetValidation = useCallback(() => {
    validatorRef.current.reset();
    setValidationErrors({});
  }, []);

  return {
    addFieldValidation,
    validateField,
    validateAllFields,
    resetValidation,
    validationErrors,
  };
}

// Field highlight hook
export function useFieldHighlight<T extends FieldValues>(
  errors: FieldErrors<T>,
  touchedFields: Set<Path<T>>
) {
  const getFieldState = useCallback(
    (field: Path<T>) => {
      const hasError = !!errors[field];
      const isTouched = touchedFields.has(field);

      return {
        hasError,
        isTouched,
        showError: hasError && isTouched,
        className: hasError && isTouched ? 'field-error' : '',
      };
    },
    [errors, touchedFields]
  );

  return { getFieldState };
}

// Success indicator hook
export function useSuccessIndicator<T extends FieldValues>(
  watch: UseFormWatch<T>,
  options?: {
    showAfterMs?: number;
    hideAfterMs?: number;
  }
) {
  const [successFields, setSuccessFields] = useState<Set<Path<T>>>(new Set());
  const timers = useRef<Map<Path<T>, ReturnType<typeof setTimeout>>>(new Map());

  const showSuccess = useCallback(
    (field: Path<T>) => {
      // Clear any existing hide timer
      const hideTimer = timers.current.get(`hide-${field}` as Path<T>);
      if (hideTimer) {
        clearTimeout(hideTimer);
      }

      // Set show timer
      const showTimer = setTimeout(() => {
        setSuccessFields((prev) => new Set(prev).add(field));

        // Set hide timer if configured
        if (options?.hideAfterMs) {
          const hideTimer = setTimeout(() => {
            setSuccessFields((prev) => {
              const newSet = new Set(prev);
              newSet.delete(field);
              return newSet;
            });
          }, options.hideAfterMs);

          timers.current.set(`hide-${field}` as Path<T>, hideTimer);
        }
      }, options?.showAfterMs || 0);

      timers.current.set(`show-${field}` as Path<T>, showTimer);
    },
    [options?.showAfterMs, options?.hideAfterMs]
  );

  const hideSuccess = useCallback((field: Path<T>) => {
    // Clear any timers
    const showTimer = timers.current.get(`show-${field}` as Path<T>);
    const hideTimer = timers.current.get(`hide-${field}` as Path<T>);

    if (showTimer) clearTimeout(showTimer);
    if (hideTimer) clearTimeout(hideTimer);

    setSuccessFields((prev) => {
      const newSet = new Set(prev);
      newSet.delete(field);
      return newSet;
    });
  }, []);

  useEffect(() => {
    const currentTimers = timers.current;
    return () => {
      // Clear all timers on unmount
      currentTimers.forEach((timer) => clearTimeout(timer));
    };
  }, []);

  return {
    successFields,
    showSuccess,
    hideSuccess,
    isSuccess: (field: Path<T>) => successFields.has(field),
  };
}

// Error summary hook
export function useErrorSummary<T extends FieldValues>(
  errors: FieldErrors<T>,
  fieldLabels?: Record<Path<T>, string>
) {
  const errorSummary = Object.entries(errors).map(([field, error]) => ({
    field: field as Path<T>,
    message: (error as { message?: string })?.message || 'Validation error',
    label: fieldLabels?.[field as Path<T>] || field,
  }));

  const hasErrors = errorSummary.length > 0;

  const scrollToError = useCallback((field: Path<T>) => {
    const element = document.querySelector(`[name="${field}"]`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      (element as HTMLElement).focus();
    }
  }, []);

  return {
    errorSummary,
    hasErrors,
    errorCount: errorSummary.length,
    scrollToError,
  };
}

// Zod validation hook
export function useZodValidation<T extends z.ZodType>(
  schema: T,
  options?: {
    mode?: 'onChange' | 'onBlur' | 'onSubmit';
    reValidateMode?: 'onChange' | 'onBlur' | 'onSubmit';
  }
) {
  const validate = useCallback(
    async (data: z.infer<T>) => {
      try {
        const result = await schema.safeParseAsync(data);

        if (result.success) {
          return { isValid: true, data: result.data };
        } else {
          const errors = result.error.issues.reduce(
            (acc, error) => {
              const path = error.path.join('.');
              acc[path] = error.message;
              return acc;
            },
            {} as Record<string, string>
          );

          return { isValid: false, errors };
        }
      } catch {
        return {
          isValid: false,
          errors: { _root: 'Validation error occurred' },
        };
      }
    },
    [schema]
  );

  return { validate, mode: options?.mode, reValidateMode: options?.reValidateMode };
}

// Validation performance hook
export function useValidationPerformance() {
  const [metrics, setMetrics] = useState({
    validationCount: 0,
    averageTime: 0,
    lastValidationTime: 0,
  });

  const measureValidation = useCallback(async <T>(validation: () => Promise<T> | T): Promise<T> => {
    const start = performance.now();
    const result = await validation();
    const end = performance.now();

    const time = end - start;

    setMetrics((prev) => ({
      validationCount: prev.validationCount + 1,
      averageTime: (prev.averageTime * prev.validationCount + time) / (prev.validationCount + 1),
      lastValidationTime: time,
    }));

    return result;
  }, []);

  return { metrics, measureValidation };
}
